import React, { useMemo } from 'react';
import { Stack } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import { AllocationResource } from '../hooks/useAllocation';
import { AllocationPieChart } from './allocation-pie-chart';
import { AllocationBarChart } from './allocation-bar-chart';
import { AppCard } from '@/components/app-card/app-card';
import { useTimeMachineUtcNow } from '@/hooks/useTimeMachineUtcNow';

interface AllocationChartsProps {
  data: {
    resources: AllocationResource[];
  };
  loading: boolean;
  currency?: string;
  cloudAccountFilter?: string;
  resourceOwnerFilter?: string;
}

function AllocationCharts({
  data,
  loading,
  currency = 'USD',
  cloudAccountFilter,
  resourceOwnerFilter,
}: AllocationChartsProps) {
  const { utcNow } = useTimeMachineUtcNow();
  const monthLabel = useMemo(() => {
    const d = utcNow ?? new Date();
    const y = d.getUTCFullYear();
    const m = d.getUTCMonth();
    return new Date(Date.UTC(y, m, 1)).toLocaleString('en-US', {
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    });
  }, [utcNow]);

  const chartData = useMemo(() => {
    let resources = data.resources || [];

    // Apply filters
    if (cloudAccountFilter) {
      resources = resources.filter(
        (r) => r.cloud_account_id?.toString() === cloudAccountFilter,
      );
    }

    if (resourceOwnerFilter) {
      resources = resources.filter(
        (r) => r.owner_id?.toString() === resourceOwnerFilter,
      );
    }

    const resourceOwnerAllocated = resources.filter(
      (r) => r.owner_id !== null,
    ).length;
    const resourceOwnerUnallocated = resources.filter(
      (r) => r.owner_id === null,
    ).length;
    const systemAllocated = resources.filter(
      (r) => r.system_id !== null,
    ).length;
    const systemUnallocated = resources.filter(
      (r) => r.system_id === null,
    ).length;

    const resourceOwnerAllocatedCost = resources
      .filter((r) => r.owner_id !== null)
      .reduce((sum, r) => sum + r.cost, 0);
    const resourceOwnerUnallocatedCost = resources
      .filter((r) => r.owner_id === null)
      .reduce((sum, r) => sum + r.cost, 0);
    const systemAllocatedCost = resources
      .filter((r) => r.system_id !== null)
      .reduce((sum, r) => sum + r.cost, 0);
    const systemUnallocatedCost = resources
      .filter((r) => r.system_id === null)
      .reduce((sum, r) => sum + r.cost, 0);

    const topUnallocatedResourceOwner = resources
      .filter((r) => r.owner_id === null)
      .sort((a, b) => b.cost - a.cost)
      .slice(0, 20)
      .map((r) => ({
        id: r.id,
        name: r.resource_name,
        type: r.resource_type,
        cost: r.cost,
        subscription: r.subscription_name,
        resourceGroup: r.resource_group_name,
        cloudAccount: r.cloud_account_name,
        currency: r.currency,
      }));

    const topUnallocatedSystem = resources
      .filter((r) => r.system_id === null)
      .sort((a, b) => b.cost - a.cost)
      .slice(0, 20)
      .map((r) => ({
        id: r.id,
        name: r.resource_name,
        type: r.resource_type,
        cost: r.cost,
        subscription: r.subscription_name,
        resourceGroup: r.resource_group_name,
        cloudAccount: r.cloud_account_name,
        currency: r.currency,
      }));

    const unallocatedByAccountOwner = resources
      .filter((r) => r.owner_id === null)
      .reduce((acc, r) => {
        const ownerName = r.cloud_account_owner_name || 'Unknown';
        if (!acc[ownerName]) {
          acc[ownerName] = { cost: 0, count: 0 };
        }
        acc[ownerName].cost += r.cost;
        acc[ownerName].count += 1;
        return acc;
      }, {} as Record<string, { cost: number; count: number }>);

    const unallocatedByAccountOwnerArray = Object.entries(
      unallocatedByAccountOwner,
    )
      .map(([ownerName, data]) => ({
        ownerName,
        cost: data.cost,
        resourceCount: data.count,
        currency,
      }))
      .sort((a, b) => b.cost - a.cost);

    const unallocatedByResourceOwner = resources
      .filter((r) => r.system_id === null)
      .reduce((acc, r) => {
        const ownerName = r.owner_name || 'Unassigned';
        if (!acc[ownerName]) {
          acc[ownerName] = { cost: 0, count: 0 };
        }
        acc[ownerName].cost += r.cost;
        acc[ownerName].count += 1;
        return acc;
      }, {} as Record<string, { cost: number; count: number }>);

    const unallocatedByResourceOwnerArray = Object.entries(
      unallocatedByResourceOwner,
    )
      .map(([ownerName, data]) => ({
        ownerName,
        cost: data.cost,
        resourceCount: data.count,
        currency,
      }))
      .sort((a, b) => b.cost - a.cost);

    return {
      pieChartData: {
        resourceOwnerCount: {
          allocated: resourceOwnerAllocated,
          unallocated: resourceOwnerUnallocated,
        },
        systemCount: {
          allocated: systemAllocated,
          unallocated: systemUnallocated,
        },
        resourceOwnerCost: {
          allocated: resourceOwnerAllocatedCost,
          unallocated: resourceOwnerUnallocatedCost,
        },
        systemCost: {
          allocated: systemAllocatedCost,
          unallocated: systemUnallocatedCost,
        },
      },
      topUnallocatedResourceOwner,
      topUnallocatedSystem,
      unallocatedByAccountOwnerArray,
      unallocatedByResourceOwnerArray,
    };
  }, [data.resources, currency, cloudAccountFilter, resourceOwnerFilter]);

  const direction = { xs: 'column', md: 'row' } as const;
  const cardSx = {
    width: '100%',
    '& > div': {
      width: { xs: '100%', lg: 'calc(50% - 8px)' },
      flex: { lg: '1 1 0px' },
      minWidth: 0,
    },
  };

  return (
    <Stack direction="column" spacing={2} sx={{ width: '100%', mt: 2 }}>
      {/* FIRST ROW - Pie Charts for Resource Count */}
      <Stack direction={direction} spacing={2} sx={cardSx}>
        <AppCard
          title="Missing Resource Owner allocation: resource count"
          icon={<BarChartOutlinedIcon color="primary" />}>
          <AllocationPieChart
            data={chartData.pieChartData.resourceOwnerCount}
            isLoading={loading}
            currency={currency}
            isCostChart={false}
          />
        </AppCard>
        <AppCard
          title="Missing System allocation: resource count"
          icon={<PersonIcon color="primary" />}>
          <AllocationPieChart
            data={chartData.pieChartData.systemCount}
            isLoading={loading}
            currency={currency}
            isCostChart={false}
          />
        </AppCard>
      </Stack>

      {/* SECOND ROW - Pie Charts for Cost */}
      <Stack direction={direction} spacing={2} sx={cardSx}>
        <AppCard
          title={`Missing Resource Owner allocation: resource cost (${monthLabel} forecast)`}
          icon={<BarChartOutlinedIcon color="primary" />}>
          <AllocationPieChart
            data={chartData.pieChartData.resourceOwnerCost}
            isLoading={loading}
            currency={currency}
            isCostChart={true}
          />
        </AppCard>
        <AppCard
          title={`Missing System allocation: resource cost (${monthLabel} forecast)`}
          icon={<PersonIcon color="primary" />}>
          <AllocationPieChart
            data={chartData.pieChartData.systemCost}
            isLoading={loading}
            currency={currency}
            isCostChart={true}
          />
        </AppCard>
      </Stack>

      {/* THIRD ROW - Top 20 Bar Charts */}
      <Stack direction={direction} spacing={2} sx={cardSx}>
        <AppCard
          title="Top 20 resources missing Resource Owner allocation by cost"
          icon={<BarChartOutlinedIcon color="primary" />}>
          <AllocationBarChart
            data={chartData.topUnallocatedResourceOwner}
            isLoading={loading}
            currency={currency}
          />
        </AppCard>
        <AppCard
          title="Top 20 resources missing System allocation by cost"
          icon={<PersonIcon color="primary" />}>
          <AllocationBarChart
            data={chartData.topUnallocatedSystem}
            isLoading={loading}
            currency={currency}
          />
        </AppCard>
      </Stack>

      {/* FOURTH ROW - Unallocated by Owner Charts */}
      <Stack direction={direction} spacing={2} sx={cardSx}>
        <AppCard
          title="Resources missing Resource Owner allocation: cost by Cloud Account Owner"
          icon={<BarChartOutlinedIcon color="primary" />}>
          <AllocationBarChart
            data={chartData.unallocatedByAccountOwnerArray}
            isLoading={loading}
            currency={currency}
          />
        </AppCard>
        <AppCard
          title="Resources missing System allocation: cost by Resource Owner"
          icon={<PersonIcon color="primary" />}>
          <AllocationBarChart
            data={chartData.unallocatedByResourceOwnerArray}
            isLoading={loading}
            currency={currency}
          />
        </AppCard>
      </Stack>
    </Stack>
  );
}

export default AllocationCharts;
